import styled, { css } from 'styled-components';

import { listItemTokens } from '../../tokens/components/ListItem/tokens';
import { ornamentTokens } from '../../tokens/components/Ornament/tokens';

import { StyledList } from '../List/List';
import { StyledListBody } from '../ListBody/ListBody';
import { MobileControlWrapper } from '../ListControl/styles';
import { BlockBodyWrapper } from '../Block/styles';

const setGridDisplay = css`
  display: -ms-grid;
  display: grid;
`;

const setGridTemplate = css`
  -ms-grid-columns: auto auto;
  -ms-grid-rows: auto;
  grid-template-columns: auto auto;
  grid-template-rows: auto;
  grid-column-gap: 1rem;
  grid-row-gap: 1rem;
`;

const setMobileGrid = css`
  grid-template-columns: 1fr;
  grid-column-gap: 0;
`;

const setGridTemplateBanner = css`
  -ms-grid-columns: auto;
  -ms-grid-rows: null;
  grid-template-columns: auto;
  grid-template-rows: null;
  grid-column-gap: null;
  grid-row-gap: null;
`;

const ornamentSizes = {
  large: ornamentTokens.BackgroundSizeLargeDefault,
  medium: ornamentTokens.BackgroundSizeMediumDefault,
  small: ornamentTokens.BackgroundSizeSmallDefault,
} as const;

const spacing = {
  large: {
    padding: listItemTokens.ContainerSpacingInnerBottomLargeDefault,
    margin: listItemTokens.ContainerSpacingOuterBottomLargeDefault,
  },
  medium: {
    padding: listItemTokens.ContainerSpacingInnerBottomMediumDefault,
    margin: listItemTokens.ContainerSpacingOuterBottomMediumDefault,
  },
  small: {
    padding: listItemTokens.ContainerSpacingInnerBottomSmallDefault,
    margin: listItemTokens.ContainerSpacingOuterBottomSmallDefault,
  },
} as const;

const positionFix = {
  large: '5px',
  medium: '6px',
  small: '7px',
} as const;

const listItem = css`
  ${setGridDisplay}
  ${setGridTemplate}

  align-items: baseline;
  justify-content: space-between;
  position: relative;

  &:last-child::after {
    content: none;
  }

  ${({ theme }) => css`
    margin-bottom: ${spacing[theme.listItemSize].margin};
    padding: ${spacing[theme.listItemSize].padding} ${listItemTokens.ContainerSpacingInnerHorizontalBaseDefault};
    padding-top: ${listItemTokens.ContainerSpacingInnerTopBaseDefault};
  `}

  ${({ theme }) =>
    theme.listItemMobile &&
    css`
      ${setMobileGrid}
    `}

  ${({ theme }) =>
    theme.listHasDivider
      ? css`
          &::after {
            background-color: ${listItemTokens.DividerColorBaseDefault};
            bottom: 0;
            content: '';
            height: ${listItemTokens.DividerHeightBaseDefault};
            position: absolute;
            right: 0;
            width: ${listItemTokens.DividerWidthBaseDefault};
          }
        `
      : css`
          padding: 0;
        `}

  ${({ theme }) =>
    theme.listItemIndent &&
    css`
      margin-left: calc(
        (${ornamentSizes[theme.listItemSize]} * ${theme.listItemIndent}) +
          (${listItemTokens.ContentSpacingLeftBaseDefault} * ${theme.listItemIndent})
      );
    `}

  ${({ theme }) =>
    theme.listItemBanner &&
    css`
      ${setGridTemplateBanner}

      ${BlockBodyWrapper} > ${StyledList} > &:first-child {
        margin-top: -19px;
      }

      background-color: ${listItemTokens.ContainerBackgroundColorBannerDefault};
      border-bottom: ${listItemTokens.ContainerBorderWidthBannerDefault}
        ${listItemTokens.ContainerBorderStyleBannerDefault} ${listItemTokens.ContainerBorderColorBannerDefault};
      border-top: ${listItemTokens.ContainerBorderWidthBannerDefault}
        ${listItemTokens.ContainerBorderStyleBannerDefault} ${listItemTokens.ContainerBorderColorBannerDefault};
      color: ${listItemTokens.ContainerFontColorBannerDefault};
      margin: 0 -19px;
      margin-bottom: 16px;
      padding: 8px 1.5rem;

      &::after {
        content: none;
      }
    `}

  ${({ theme }) =>
    theme.listItemHasIcon &&
    css`
      & > ${StyledListBody} {
        margin-left: calc(${ornamentSizes[theme.listItemSize]} + ${listItemTokens.ContentSpacingLeftBaseDefault});
      }

      ${theme.listItemMobile &&
      css`
        ${MobileControlWrapper} {
          margin-left: calc(${ornamentSizes[theme.listItemSize]} + ${listItemTokens.ContentSpacingLeftBaseDefault});
        }
      `}

      &::after {
        width: calc(100% - (${ornamentSizes[theme.listItemSize]} + ${listItemTokens.ContentSpacingLeftBaseDefault}));
      }

      &::last-child::after {
        content: none;
      }
    `}

  ${StyledList} &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  ${({ theme }) =>
    theme.listConnected &&
    css`
      &::before {
        background-color: ${listItemTokens.ConnectorColorBaseDefault};
        bottom: -21px;
        content: '';
        left: calc(${ornamentSizes[theme.listItemSize]} / 2);
        position: absolute;
        top: 8px;
        width: 1px;
      }

      &:last-child::before {
        content: none;
      }

      ${({ theme }) =>
        theme.listItemBanner &&
        css`
          &::before {
            content: none;
          }
        `}
    `}
`;

/* @internal */
export const OrnamentWrapper = styled.div`
  ${({ theme }) =>
    theme.listItemHasText &&
    css`
      margin-top: ${positionFix[theme.listItemSize]};
    `}
`;

/* @internal */
export const ListItemWrapper = styled.li`
  ${listItem}
`;

/* @internal */
export const StyledListItem = styled.div`
  align-self: flex-start;
  display: inline-flex;
  height: 100%;
  position: relative;
  vertical-align: middle;
`;

/* @internal */
export const ListItemInfo = styled.div`
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  padding-left: ${listItemTokens.ContentSpacingLeftBaseDefault};

  &:only-child {
    padding-left: 0;
  }
`;
